import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutInfo, faqItems } from '@/mocks/about';

export default function AboutPage() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: aboutInfo.brandName,
    url: 'https://baoliaowang.com',
    description: aboutInfo.introduction,
    email: aboutInfo.contact.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '建国路88号SOHO现代城A座',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      telephone: aboutInfo.contact.phone,
      email: aboutInfo.contact.email,
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://baoliaowang.com' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 爆料网"
        description={`${aboutInfo.brandName} - ${aboutInfo.subtitle}。${aboutInfo.introduction}`}
        keywords="爆料网,关于我们,爆料平台,品牌介绍,联系我们"
        canonicalUrl="https://baoliaowang.com/about"
      />
      <SchemaOrg schema={aboutSchema} />
      <SchemaOrg schema={faqSchema} />
      <SchemaOrg schema={breadcrumbSchema} />

      <main className="pt-20">
        <section className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '关于我们' }]} />
            <div className="py-10 md:py-16 text-center">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                关于 <strong className="text-primary-500">爆料网</strong>
              </h1>
              <p className="text-lg text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                {aboutInfo.subtitle}
              </p>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
              <h2 className="text-2xl font-heading font-bold text-foreground-900">品牌介绍</h2>
            </div>
            <p className="text-foreground-700 leading-relaxed text-base mb-6">
              {aboutInfo.introduction}
            </p>
            <p className="text-foreground-700 leading-relaxed text-base mb-6">
              {aboutInfo.mission}
            </p>
            <p className="text-foreground-700 leading-relaxed text-base">
              {aboutInfo.vision}
            </p>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              {aboutInfo.stats.map((stat) => (
                <div key={stat.label} className="text-center p-6 bg-background-50 rounded-lg">
                  <div className="text-3xl md:text-4xl font-heading font-bold text-primary-500 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
              <h2 className="text-2xl font-heading font-bold text-foreground-900">核心价值观</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {aboutInfo.values.map((val) => (
                <div key={val.title} className="flex gap-4 p-5 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                    <i className={`${val.icon} text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground-900 mb-1 text-sm">{val.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{val.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-1 h-6 bg-secondary-500 rounded-full"></div>
              <h2 className="text-2xl font-heading font-bold text-foreground-900">口碑评价</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {aboutInfo.reputation.map((rep, idx) => (
                <div key={idx} className="p-6 bg-background-50 rounded-lg">
                  <div className="flex items-center gap-1 mb-4 text-accent-500">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-foreground-700 leading-relaxed text-sm mb-4">
                    &ldquo;{rep.quote}&rdquo;
                  </p>
                  <div>
                    <div className="font-semibold text-foreground-900 text-sm">{rep.source}</div>
                    <div className="text-xs text-foreground-500">{rep.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
              <h2 className="text-2xl font-heading font-bold text-foreground-900">联系我们</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <div className="font-medium text-foreground-900 text-sm">电子邮箱</div>
                    <a href={`mailto:${aboutInfo.contact.email}`} className="text-sm text-foreground-600 hover:text-primary-500 transition-colors">
                      {aboutInfo.contact.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-wechat-line"></i>
                  </div>
                  <div>
                    <div className="font-medium text-foreground-900 text-sm">微信公众号</div>
                    <span className="text-sm text-foreground-600">{aboutInfo.contact.wechat}</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-weibo-line"></i>
                  </div>
                  <div>
                    <div className="font-medium text-foreground-900 text-sm">微博</div>
                    <span className="text-sm text-foreground-600">{aboutInfo.contact.weibo}</span>
                  </div>
                </div>
              </div>
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-phone-line"></i>
                  </div>
                  <div>
                    <div className="font-medium text-foreground-900 text-sm">联系电话</div>
                    <span className="text-sm text-foreground-600">{aboutInfo.contact.phone}</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div>
                    <div className="font-medium text-foreground-900 text-sm">办公地址</div>
                    <span className="text-sm text-foreground-600">{aboutInfo.contact.address}</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-time-line"></i>
                  </div>
                  <div>
                    <div className="font-medium text-foreground-900 text-sm">工作时间</div>
                    <span className="text-sm text-foreground-600">{aboutInfo.contact.workingHours}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}